import { Check, Loader2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import {
  COLOR_HEX,
  COLOR_HEX_DARK,
  COLOR_LABEL,
  PALETTE,
  type PaletteColor,
} from "@trip/domain/palette";
import { ApiError, patch, put, type Me, type Member, type Trip } from "../lib/api.ts";
import { useApi } from "../lib/useApi.ts";
import { Avatar, Card, ICON, reason } from "../ui/index.ts";

/**
 * Who you are on this trip: your name, and your colour.
 *
 * The two sit together because they answer the same question — which of these people am I —
 * and because the preview of one is the preview of the other: the avatar above the fields is
 * exactly what the rest of the app will draw once they are saved.
 *
 * The colour is per trip and the name is not. A name is who you are; a colour only has to tell
 * you apart from the handful of people you are travelling with, and two of them wanting red is
 * a disagreement that belongs to that trip and no other.
 */
export function YouCard({ trip, me }: { trip: Trip; me: Me }) {
  const members = useApi<{ items: Member[] }>(`/v1/trips/${trip.id}/members`);
  const you = members.data?.items.find((m) => m.isYou) ?? null;
  const taken = new Map(
    (members.data?.items ?? []).filter((m) => !m.isYou).map((m) => [m.color, m.displayName]),
  );

  const [name, setName] = useState(me.user.displayName);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The name comes from the session, which is re-read elsewhere; follow it rather than keeping
  // a stale copy of a field the person may have changed on another device.
  useEffect(() => setName(me.user.displayName), [me.user.displayName]);

  async function saveName(e: FormEvent) {
    e.preventDefault();
    const next = name.trim();
    if (next.length === 0 || next === me.user.displayName) return;
    setBusy(true);
    setError(null);
    try {
      await patch(`/v1/me`, { displayName: next });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
      await members.reload();
    } catch (err) {
      setError(reason(err as Error));
    } finally {
      setBusy(false);
    }
  }

  async function pick(color: PaletteColor) {
    if (color === you?.color) return;
    setError(null);
    try {
      await put(`/v1/trips/${trip.id}/members/me/color`, { color });
      await members.reload();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? `${taken.get(color) ?? "Someone else"} has that colour already.`
          : reason(err as Error),
      );
    }
  }

  return (
    <Card title="You on this trip">
      <div className="you-head">
        <Avatar name={name || me.user.displayName} color={you?.color ?? null} />
        <span className="you-name">{name || me.user.displayName}</span>
      </div>

      <form className="row wrap" onSubmit={(e) => void saveName(e)}>
        <label className="grow">
          <span className="label">Your name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            aria-label="Your name"
          />
        </label>
        <button className="button" disabled={busy || name.trim() === me.user.displayName}>
          {busy ? (
            <Loader2 {...ICON} aria-hidden="true" className="spin" />
          ) : saved ? (
            <Check {...ICON} aria-hidden="true" />
          ) : null}
          {saved ? "Saved" : "Save name"}
        </button>
      </form>
      <p className="muted small" style={{ margin: 0 }}>
        Your name is the same on every trip you are on.
      </p>

      <fieldset className="color-pool">
        <legend className="label">Your colour on this trip</legend>
        {PALETTE.map((c) => {
          const held = taken.get(c);
          return (
            <button
              key={c}
              type="button"
              className="pool-swatch"
              // From the one palette, never a second copy of it in CSS. Both themes go down
              // together because CSS, not this component, knows which one is on.
              style={
                { "--swatch": COLOR_HEX[c], "--swatch-dark": COLOR_HEX_DARK[c] } as React.CSSProperties
              }
              data-on={you?.color === c}
              disabled={held !== undefined}
              aria-pressed={you?.color === c}
              // Said in words, because a colour is the one thing a swatch cannot say to
              // somebody who cannot see it.
              aria-label={held ? `${COLOR_LABEL[c]}, taken by ${held}` : COLOR_LABEL[c]}
              title={held ? `${held} has this one` : COLOR_LABEL[c]}
              onClick={() => void pick(c)}
            >
              {you?.color === c && <Check size={16} strokeWidth={3} aria-hidden="true" />}
            </button>
          );
        })}
      </fieldset>
      <p className="muted small" style={{ margin: 0 }}>
        It draws your route on the map and marks everything you record.
        {you && !you.colorChosen && " Yours came from the order the trip filled up."}
      </p>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </Card>
  );
}
